<template>
    <ion-page>
        <ion-header>
            <ion-toolbar>
                <ion-buttons slot="end">
                    <ion-button @click="close(item)">
                        <ion-icon slot="icon-only" :icon="closeOutline"></ion-icon>
                    </ion-button>
                </ion-buttons>
                <ion-title v-if="mode">{{ mode.toUpperCase() }} ITEM</ion-title>
            </ion-toolbar>
        </ion-header>
        <ion-content :fullscreen="true">
            <ion-segment v-model="mode" v-if="showSwitcher">
                <ion-segment-button value="return">Return</ion-segment-button>
                <ion-segment-button value="move">Move</ion-segment-button>
            </ion-segment>
            <ion-list>
                <ion-item>
                    <ion-label>
                        <h2>{{ item.name }}</h2>
                        <p>{{ item.item_number }} </p>
                    </ion-label>
                </ion-item>
                <ion-item v-if="source && ['move', 'pick', 'stack'].indexOf(mode) > -1">
                    <ion-label>
                        <h2>
                            Source Location
                        </h2>
                        <p>
                            {{ source.code }}
                            <small v-if="!sourceConfirmed && needsSourceConfirmed">(scan to confirm)</small>
                            <ion-icon v-else slot="icon-only" color="success" :icon="checkmarkCircle"/>
                        </p>
                    </ion-label>
                    <ion-button size="medium" color="primary" @click="openLocationScanner('source')">
                        <ion-icon slot="icon-only" :icon="barcode"/>
                    </ion-button>
                    <ion-button v-if="web" expand="block" color="primary" size="medium" @click="openLocationPicker('source')">
                        <ion-icon slot="icon-only" :icon="search"/>
                    </ion-button>
                </ion-item>
                <ion-item v-if="['move', 'return', 'stack'].indexOf(mode) > -1">
                    <ion-label class="ion-text-wrap">
                        <h2>Target Location</h2>
                        <p v-if="!location.code">Scan or search</p>
                        <p>
                            {{ location.code }}
                            <span v-if="mode === 'return'">
                                <small v-if="!targetConfirmed">(scan to confirm)</small>
                                <ion-icon v-else slot="icon-only" color="success" :icon="checkmarkCircle"/>
                            </span>
                        </p>
                    </ion-label>
                    <ion-button size="medium" color="primary" @click="openLocationScanner('target')">
                        <ion-icon slot="icon-only" :icon="barcode"/>
                    </ion-button>
                    <ion-button v-if="mode === 'move' || mode === 'stack'" expand="block" color="primary" size="medium" @click="chooseLocationFromCurrent()">
                        <ion-icon slot="icon-only" :icon="listOutline"/>
                    </ion-button>
                    <ion-button v-if="web" expand="block" color="primary" size="medium" @click="openLocationPicker('target')">
                        <ion-icon slot="icon-only" :icon="search"/>
                    </ion-button>
                </ion-item>
                <ion-item v-if="mode === 'return'">
                    <ion-label>
                        Return as B-Stock
                    </ion-label>
                    <ion-checkbox v-model="bStock" slot="end" />
                </ion-item>
                <div v-else>
                <ion-item>
                    <ion-label>B-Stock?</ion-label>
                    <ion-checkbox v-model="bStock" />
                </ion-item>
                </div>
                <div v-if="mode === 'pick'">
                    <ion-item lines="none" class="ion-margin-top">
                        <big-stepper label="Quantity Picked" v-model="quantity"/>
                    </ion-item>
                </div>
                <div v-if="mode === 'move' || mode === 'stack'">
                    <ion-item lines="none" class="ion-margin-top">
                        <big-stepper label="Quantity Moved" v-model="quantity"/>
                    </ion-item>
                    <ion-item>
                        <ion-label><h2>Box Calculator</h2></ion-label>
                    </ion-item>
                    <ion-item lines="none">
                        <big-stepper v-model="caseQty" label="Case Quantity" size="small" />
                    </ion-item>
                    <ion-item lines="none">
                        <big-stepper v-model="boxesLikeThis" label="Boxes like this" size="small" />
                    </ion-item>
                </div>
                <div v-if="mode === 'return'">
                    <ion-item lines="none" class="ion-margin-top">
                        <big-stepper label="Quantity Returned" v-model="quantity"/>
                    </ion-item>
                </div>
                <div v-if="mode === 'pick'">
                    <ion-item lines="none">
                        <ion-label>Reason</ion-label>
                        <ion-select v-model="reason">
                            <ion-select-option value="Office">Office</ion-select-option>
                            <ion-select-option value="Sample">Sample</ion-select-option>
                            <ion-select-option value="Unit">Unit</ion-select-option>
                            <ion-select-option value="Customer Order">Customer Order</ion-select-option>
                            <ion-select-option value="Correcting Stock" v-if="isAdmin">Correcting Stock</ion-select-option>
                        </ion-select>
                    </ion-item>
                </div>
                <div v-if="mode === 'return'">
                    <ion-item lines="none">
                        <ion-label>Reason</ion-label>
                        <stock-return-reason-select v-model="reason" />
                    </ion-item>
                </div>
            </ion-list>
        </ion-content>
        <ion-footer v-if="mode === 'move' || mode === 'stack' || mode === 'return'">
            <ion-button size="large" color="secondary" expand="full" @click="save()" :disabled="!canProceed">{{ confirmLabel }}</ion-button>
        </ion-footer>
        <ion-footer v-if="mode === 'pick'">
            <ion-button size="large" color="secondary" expand="full" @click="save()" :disabled="!canProceed">Pick</ion-button>
        </ion-footer>
    </ion-page>
</template>

<script>
import {modalController, pickerController} from '@ionic/vue';
import {Capacitor} from '@capacitor/core';
import {
    IonContent, IonPage, IonLabel, IonItem, IonHeader, IonToolbar,
    IonTitle, IonButtons, IonButton, IonIcon, IonFooter, IonList,
    IonCheckbox, IonSegment, IonSegmentButton, IonSelect, IonSelectOption
} from '@ionic/vue';

import EventBus from "@/EventBus";
import BigStepper from "@/components/BigStepper";
import LocationSearchModalHelper from "@/services/LocationSearchModalHelper";
import {closeOutline, search, barcode, listOutline, checkmarkCircle} from 'ionicons/icons';
import Storage from "@/services/Storage";
import StockReturnReasonSelect from "@/components/StockReturnReasonSelect";
import {map} from "lodash";
export default {
    props: {
        initMode: String,
        item: Object,
        source: Object,
        confirmLabel: {
            default: 'Item Stacked'
        }},
    inject: ['$http', '$validationAlert', '$scanner'],
    name: 'MoveModal',
    components: {
        StockReturnReasonSelect,
        BigStepper,
        IonContent, IonPage, IonLabel, IonItem, IonHeader, IonToolbar,
        IonTitle, IonButtons, IonButton, IonIcon, IonFooter, IonList,
        IonSegment, IonSegmentButton, IonCheckbox, IonSelect, IonSelectOption
    },
    watch: {
        boxesLikeThis() {
            this.quantity = this.caseQty * this.boxesLikeThis;
        }
    },
    mounted() {
        this.storage.get('user').then(user => {
            this.user = user;
        });
        if (!this.initMode) {
            this.mode = 'move';
            this.showSwitcher = true;
        } else {
            this.mode = this.initMode;
            if (this.mode === 'return') {
                // we don't have a 'source' location for returns,
                // but this component only has a source prop, so
                // we'll copy source -> location and have the user
                // confirm that ...

                // OLD BEHAVIOUR - No longer required.

                // console.log("Copying source to location for return");
                // this.location = this.source;
            }
        }
    },
    computed: {
        isAdmin() {
            return this.user &&
                this.user.permissions &&
                this.user.permissions['warehouse.manage'];
        },
        web() {
            return Capacitor.platform === 'web';
        },
        canProceed() {

            if (this.loading) {
                return false;
            }

            if (this.mode === 'pick') {
                return (this.needsSourceConfirmed?this.sourceConfirmed:true) &&
                    this.quantity > 0 &&
                    this.source.id > 0;
            }

            if (this.mode === 'move' || this.mode === 'stack' || this.mode == 'return') {

                return (this.needsSourceConfirmed?this.sourceConfirmed:true) &&
                    this.quantity > 0 &&
                    this.source.id > 0 &&
                    this.location.id > 0;
            }

            return this.quantity > 0 && this.location.id > 0;
        },
        /**
         * Don't require confirmation of source from temp, returns or unknown sources,
         * regardless of scenario.
         */
        needsSourceConfirmed() {
            if (this.source && (this.source.is_temporary || this.source.is_unknown || this.source.is_returns)) {
                return false;
            }
            return this.mode === 'pick' || this.mode === 'move' || this.mode === 'stack';
        },
        params() {
            return this.$route.params;
        }
    },
    methods: {
        openLocationScanner(type) {

            if (this.web && type === 'source') {
                this.sourceConfirmed = true;
                return;
            }

            if (this.web && type === 'target') {
                this.targetConfirmed = true;
                return;
            }


            this.$scanner.scan('location').then(result => {
                if (type === 'source' && this.source && this.source.id) {
                    this.sourceConfirmed = (parseInt(result.extra.id) === this.source.id);
                } else if (type === 'target' && this.mode === 'return' && this.location && this.location.id) {
                    this.targetConfirmed = (parseInt(result.extra.id) === this.location.id);
                } else {
                    this.location = result.extra;
                }
            });
        },
        openLocationPicker(type) {
            LocationSearchModalHelper.open().then(location => {
                if (type === 'source') {
                    this.sourceConfirmed = (location.data.id === this.source.id);
                } else if (type === 'target' && this.mode === 'return') {
                    console.log("Confirming location target for return... %o, %o", location, this.location);
                    this.targetConfirmed = (location.data.id === this.location.id);
                } else {
                    this.location = location.data;
                }
            });
        },
        async chooseLocationFromCurrent() {
            const picker = await pickerController.create({
                buttons: [{text: 'Close Preview', role: 'cancel'}],
                columns: [{
                    name: 'location',
                    options: map(this.item.locations, location => {
                        console.log({...location});
                        const label = `${location.code} (${location.pivot.qty_free})`;
                        return {
                            text: label
                        }
                    })
                }]
            });
            await picker.present();
            picker.onDidDismiss().then(value => {
                this.location = value.data.location.value;
            });


        },
        close(item) {
            modalController.dismiss(item);
        },
        /**
         * Update the shipment line. By the time we call this
         * we've already sought the reason and comment if
         * required. They'll be passed in ...
         *
         */
        save() {
            this.loading = true; // disable to be sure
            EventBus().emitter.emit('loading', true);
            // send through a payload which contains: picked (count),
            // reason (object with id as a key), comment (string)
            this.$http.put(`/api/stack/items/${this.item.id}`, {
                bStock: this.bStock,
                mode: this.mode,
                location: this.location,
                source: this.mode === 'return'?null:this.source,
                item: this.item,
                quantity: this.quantity,
                comment: this.reason
            }).then(() => {
                EventBus().emitter.emit('loading', false);
                // not using finally here to dismiss loading. Instead,
                // we're going to use a silly setTimeout hack ...
                // There's an event bound to when the modal is dismissed
                // which, in turn, triggers a loading event. To prevent
                // crossed wires, and overlapping loading events, we
                // try to separate out the dismiss call from the loading:false
                setTimeout(() => {
                    modalController.dismiss(this.item);
                }, 500);
            }).catch(error => {
                EventBus().emitter.emit('loading', false);
                this.$validationAlert(error);
            }).finally(() => {
                this.loading = false;
            });
        }
    },
    data() {
        return {
            user: {},
            reason: '',
            sourceConfirmed: false,
            targetConfirmed: false,
            showSwitcher: false,
            bStock: false,
            mode: 'move',
            caseQty: this.item.case_qty,
            boxesLikeThis: 0,
            loading: false,
            quantity: 0,
            location: {}
        }
    },
    setup() {
        const storage = new Storage();
        return {
            checkmarkCircle,
            closeOutline,
            search,
            barcode,
            listOutline,
            storage
        };
    }
}
</script>
<style scoped>
ion-input {
    font-size: 200%;
}
</style>

